import { VideoTrackDestination } from './video-node/destination';
import { TextSourceNode, VideoImageSourceNode, VideoPlayerSourceNode, VideoTrackSourceNode } from './video-node/source';
import { VideoNodeOptions } from './video-node/base';
import Logger from '../utils/log/logger';
import { ChangeState, FSM, FSMError } from 'afsm';
import { VideoPlayer } from '../common/player';
import kvStatManager, { KV_REPORT_KEY_VIDEO_MANAGER } from './kv-stat-manager';
import RtcError from '../common/rtc-error';
import ErrorCode from '../common/error-code';
import { WEBGL_ATTRIBUTES } from '../common/constants';
import { isOffscreenCanvasWorkerSupported } from '../common/rtc-detection';
const vertexShaderSource = `
// 顶点着色器
attribute vec4 a_position;
attribute vec2 a_texCoord;
varying vec2 v_texCoord;

void main() {
  gl_Position = a_position;
  v_texCoord = a_texCoord;
}
`;
const fragmentShaderSource = `
// 片元着色器
precision mediump float;
varying vec2 v_texCoord;
uniform sampler2D u_texture;

void main() {
  gl_FragColor = texture2D(u_texture, v_texCoord);
} `;
export const enum VideoContextErrorCode {
  SUCCESS = 0,
  NOTSUPPORTED = 2,
  CREATE_VIDEO_NODE_FAILED,
  WEBGL_CONTEXT_LOST,
  WEBGL_ERROR,
  SEGMENTATION_FAILED,
  CANVAS_TRACK_MUTED,
  CANVAS_TRACK_ENDED,
  CONTEXT_LOST
}
abstract class VideoContext extends FSM<{ disconnect: [], unavailable: RtcError; }> {
  frameRate: number;
  _canvas?: HTMLCanvasElement;
  log: Logger;
  hasAlpha = false;
  name: string;
  static _ids = 0;
  error?: RtcError;
  constructor(options: {
    name: string;
    frameRate: number;
    logger: Logger;
  }) {
    super();
    this.name = options.name;
    this.log = options.logger.createChild({ id: `vc-${this.name}` });
    this.frameRate = options.frameRate;
  }
  get canvas() {
    return this._canvas;
  }
  set width(width: number) {
    if (this._canvas) this._canvas.width = width;
  }
  get width() {
    return this._canvas?.width || 0;
  }
  set height(height: number) {
    if (this._canvas) this._canvas.height = height;
  }
  get height() {
    return this._canvas?.height || 0;
  }
  setSize(width: number, height: number) {
    if (this._canvas) {
      this._canvas.width = width;
      this._canvas.height = height;
    }
  }
  createVideoTrackSource<T extends Video2dContext | VideoGlContext>(this: T, videoTrack: MediaStreamVideoTrack, name: string) {
    return new VideoTrackSourceNode(this, videoTrack, { logger: this.log, name });
  }
  createVideoTrackDestination<T extends Video2dContext | VideoGlContext>(this: T, options?: VideoNodeOptions) {
    return new VideoTrackDestination(this, options);
  }
  createVideoImageSource<T extends Video2dContext | VideoGlContext>(this: T, image?: TexImageSource, options?: VideoNodeOptions) {
    return new VideoImageSourceNode(this, options ? Object.assign(options, { logger: this.log, image }) : { logger: this.log, image });
  }
  createVideoPlayerSource<T extends Video2dContext | VideoGlContext>(this: T, player: VideoPlayer, options?: Omit<VideoNodeOptions, 'name'>) {
    return new VideoPlayerSourceNode(this, player, options ? Object.assign(options, { logger: this.log }) : { logger: this.log });
  }
  createTextSource(this: Video2dContext, textOptions: {
    content: string,
    font?: string,
    color?: string | CanvasGradient | CanvasPattern;
  }, options?: Omit<VideoNodeOptions, 'name'>) {
    return new TextSourceNode(this, textOptions, options ? Object.assign(options, { logger: this.log }) : { logger: this.log });
  }
  get available() {
    return this.state === 'created';
  }
  disconnect() {
    this.emit('disconnect');
  }
}
export class VideoGlContext extends VideoContext {
  defaultProgam: WebGLProgram;
  defaultVShader: WebGLShader;
  defaultFShader: WebGLShader;
  ctx?: WebGLRenderingContext;
  _canvas2d?: HTMLCanvasElement;
  static UNAVAILABLE = 'unavailable' as const;
  get canvas() {
    return this._canvas2d || this._canvas;
  }
  @ChangeState(FSM.INIT, 'created', {
    sync: true, fail(this: VideoGlContext, error: FSMError) {
      this.log.error('video gl context create failed', error.cause);
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_VIDEO_MANAGER.START_VIDEO_CONTEXT_GL, error: error.cause || error });
    }, success(this: VideoGlContext) {
      this.log.info('video context created use webgl');
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_VIDEO_MANAGER.START_VIDEO_CONTEXT_GL });
    }
  })
  create(with2dDraw = false) {
    if (!this._canvas) {
      this._canvas = document.createElement('canvas');
      this._canvas.id = `trtc_${this.name}_${VideoContext._ids++}`;
    }
    if (with2dDraw) {
      this._canvas2d = document.createElement('canvas');
    }
    this.ctx = this._canvas.getContext('webgl2', WEBGL_ATTRIBUTES) as WebGLRenderingContext;
    if (!this.ctx) {
      throw new RtcError({
        code: ErrorCode.VIDEO_MANAGER_ERROR,
        extraCode: VideoContextErrorCode.NOTSUPPORTED,
        message: 'webgl2 not supported'
      });
    }
    this.defaultVShader = this.createShader(this.ctx.VERTEX_SHADER, vertexShaderSource);
    this.defaultFShader = this.createShader(this.ctx.FRAGMENT_SHADER, fragmentShaderSource);
    this.defaultProgam = this.createProgram(this.defaultVShader, this.defaultFShader);
    this._canvas.addEventListener('webglcontextlost', () => {
      this.destroy(new RtcError({
        code: ErrorCode.VIDEO_MANAGER_ERROR,
        extraCode: VideoContextErrorCode.WEBGL_CONTEXT_LOST,
        message: 'webgl context lost'
      }));
    });
  }
  @ChangeState('created', FSM.INIT, {
    ignoreError: true, sync: true, success(this: VideoGlContext, reason) {
      if (reason) this.emit(VideoGlContext.UNAVAILABLE, reason);
      this.removeAllListeners();
    }
  })
  destroy(error?: RtcError) {
    let reason = '';
    if (error) {
      reason = error.message;
      this.error = error;
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_VIDEO_MANAGER.VIDEO_CONTEXT_GL_RESULT, error });
    }
    this.disconnect();
    this.log.info(`video context destroy${reason}` ? `: ${reason}` : '');
    if (this.ctx) {
      this.ctx.deleteShader(this.defaultVShader);
      this.ctx.deleteShader(this.defaultFShader);
      this.ctx.deleteProgram(this.defaultProgam);
      delete this.ctx;
    }
    return error;
  }
  set width(width: number) {
    this.ctx?.viewport(0, 0, width, this.height);
    super.width = width;
    if (this._canvas2d) this._canvas2d.width = width;
  }
  set height(height: number) {
    this.ctx?.viewport(0, 0, this.width, height);
    super.height = height;
    if (this._canvas2d) this._canvas2d.height = height;
  }
  setSize(width: number, height: number) {
    this.ctx?.viewport(0, 0, width, height);
    super.setSize(width, height);
    if (this._canvas2d) {
      this._canvas2d.width = width;
      this._canvas2d.height = height;
    }
  }

  createShader(type: number, source: string) {
    const gl = this.ctx!;
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return shader;
  }
  createProgram(vertexShader: WebGLShader, fragmentShader: WebGLShader) {
    const gl = this.ctx!;
    const program = gl.createProgram()!;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      this.log.error(gl.getProgramInfoLog(program));
    }
    return program;
  }
}

export class Video2dContext extends VideoContext {
  ctx?: CanvasRenderingContext2D | null;

  // Worker rendering mode properties
  private _useWorkerRendering = false;
  private _renderWorker: Worker | null = null;
  private _workerReady = false;
  private _workerFailed = false;

  // TrackGenerator output mode properties
  private _useTrackGenerator = false;
  private _generatorTrack: MediaStreamTrack | null = null;

  // Context lost recovery properties
  private _contextLostHandler: (() => void) | null = null;
  private _contextRestoredHandler: (() => void) | null = null;

  // Worker frame extraction (ReadableStream) tracking
  private _transferredStreams = new Set<string>();
  // Track sources that have been degraded from Worker ReadableStream to main thread extraction.
  // Once degraded, these sources should not be re-transferred to Worker.
  private _degradedStreams = new Set<string>();
  // Keep references to MediaStreamTrackProcessor instances to prevent GC.
  // If the processor is garbage-collected, its ReadableStream (transferred to Worker) stops producing frames.
  private _trackProcessors = new Map<string, any>();
  private _streamErrorCallback: ((id: string, message: string) => void) | null = null;

  // Pending state to sync after Worker becomes ready
  private _pendingBackground: string | null = null;
  private _pendingBackgroundImage: ImageBitmap | null = null;
  private _pendingSize: { width: number; height: number } | null = null;
  // Pending ImageSource messages buffered before Worker is ready
  private _pendingImageSourceMessages = new Map<string, { type: string; id: string; src?: string; animated?: boolean; mimeType?: string }>();

  // Callback registered by VideoDestinationNode to be invoked when Worker becomes ready
  private _onWorkerReadyCallback: (() => void) | null = null;

  /**
   * Whether this context is using Worker rendering mode and Worker is ready.
   */
  get useWorkerRendering() {
    return this._useWorkerRendering && this._workerReady;
  }

  /**
   * Whether TrackGenerator output mode is active.
   */
  get useTrackGenerator() {
    return this._useTrackGenerator;
  }

  /**
   * The MediaStreamTrack generated by MediaStreamTrackGenerator.
   * Available immediately after TrackGenerator creation (no async wait needed).
   */
  get generatorTrack() {
    return this._generatorTrack;
  }

  /**
   * Whether this context has Worker rendering mode enabled (regardless of ready state).
   * Use this to check if we should buffer pending state for Worker.
   */
  get isWorkerMode() {
    return this._useWorkerRendering;
  }

  /**
   * Whether the Worker is ready to receive messages.
   */
  get workerReady() {
    return this._workerReady;
  }

  /**
   * The render Worker instance (null if not in Worker mode).
   */
  get renderWorker() {
    return this._renderWorker;
  }

  @ChangeState(FSM.INIT, 'created', {
    sync: true, fail(this: Video2dContext, error: FSMError) {
      this.log.error('video 2d context create failed', error.cause);
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_VIDEO_MANAGER.START_VIDEO_CONTEXT_2D, error: error.cause || error });
    }, success(this: Video2dContext) {
      this.log.info(`video context created use 2d${this._useWorkerRendering ? ' (worker mode)' : ''}`);
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_VIDEO_MANAGER.START_VIDEO_CONTEXT_2D });
    }
  })
  create(options: { alpha: boolean; willReadFrequently?: boolean; workerScript?: string }) {
    this.hasAlpha = options.alpha;
    this._canvas = document.createElement('canvas');
    this._canvas.id = `trtc_${this.name}_${VideoContext._ids++}`;

    // Try Worker rendering mode only when workerScript is provided and environment supports it
    if (options.workerScript && isOffscreenCanvasWorkerSupported()) {
      try {
        this._initWorkerMode(options.workerScript);
        this.log.info('Worker rendering mode initialized');
        return;
      } catch (err) {
        this.log.warn(`Worker rendering mode init failed, falling back to main thread: ${(err as Error).message}`);
        this._cleanupWorkerMode();
        // Canvas may have been bound to an invalid context, recreate it
        this._canvas = document.createElement('canvas');
        this._canvas.id = `trtc_${this.name}_${VideoContext._ids++}`;
      }
    }

    // Fallback: main thread 2d rendering
    this._initMainThreadMode(options);
  }

  /**
   * Initialize Worker rendering mode:
   * - Create a separate OffscreenCanvas for Worker rendering
   * - Create MediaStreamTrackGenerator for direct track output
   * - Instantiate the render Worker
   *
   * Note: TrackGenerator is required for Worker mode (guaranteed by isOffscreenCanvasWorkerSupported).
   * If TrackGenerator creation fails, the exception propagates to create() which falls back to main thread mode.
   */
  private _initWorkerMode(workerScript: string) {
    // Create a separate OffscreenCanvas to transfer to Worker
    const offscreen = new OffscreenCanvas(this._canvas!.width || 640, this._canvas!.height || 480);

    // Create Worker from externally provided script blob
    this._renderWorker = this._createRenderWorker(workerScript);
    this._useWorkerRendering = true;

    // Build init message and transferables
    const initMsg: any = {
      type: 'init',
      canvas: offscreen,
      width: this._canvas!.width || 640,
      height: this._canvas!.height || 480
    };
    const transfers: Transferable[] = [offscreen];

    // Create MediaStreamTrackGenerator and pass writable to Worker
    // @ts-ignore - MediaStreamTrackGenerator may not be in TS lib
    const generator = new MediaStreamTrackGenerator({ kind: 'video' });
    this._generatorTrack = generator;
    this._useTrackGenerator = true;

    const writable = generator.writable as WritableStream;
    initMsg.writable = writable;
    transfers.push(writable as unknown as Transferable);

    this.log.info(`TrackGenerator created, track id=${generator.id}, readyState=${generator.readyState}`);
    this.log.info('Sending writable to Worker');

    // Send the OffscreenCanvas and writable to Worker
    this._renderWorker.postMessage(initMsg, transfers);

    // Listen for Worker messages
    this._renderWorker.onmessage = (event: MessageEvent) => {
      const { data } = event;
      switch (data.type) {
        case 'ready':
          this._workerReady = true;
          this.log.info('Render Worker ready');
          // Flush pending state that was set before Worker was ready
          this._flushPendingState();
          // Trigger pending start callback registered by VideoDestinationNode
          if (this._onWorkerReadyCallback) {
            const cb = this._onWorkerReadyCallback;
            this._onWorkerReadyCallback = null;
            cb();
          }
          break;
        case 'trackGeneratorFailed':
          this.log.warn(`Worker reported TrackGenerator failure, failCount=${data.failCount}`);
          this._handleTrackGeneratorDegradation();
          break;
        case 'error':
          this.log.error(`Render Worker error: ${data.message}`);
          break;
        case 'streamError':
          this.log.warn(`Worker stream error for source ${data.id}: ${data.message}`);
          this._transferredStreams.delete(data.id);
          if (this._streamErrorCallback) {
            this._streamErrorCallback(data.id, data.message);
          }
          break;
        case 'streamStalled':
          this.log.warn(`Worker stream stalled for source ${data.id}: no frames received within ${data.stallDuration}ms, degrading to main thread extraction`);
          this._transferredStreams.delete(data.id);
          // Mark this source as degraded so it won't be re-transferred to Worker
          this._degradedStreams.add(data.id);
          // Clean up the stalled pipeline in Worker
          this.removeTrackStream(data.id);
          // Notify VideoTrackDestination to degrade this source to main thread extraction
          if (this._streamErrorCallback) {
            this._streamErrorCallback(data.id, `ReadableStream stalled: no frames received within ${data.stallDuration}ms`);
          }
          break;
        case 'log': {
          const logLevel = data.level as string;
          const logMessage = data.message as string;
          const logFn = (this.log as unknown as Record<string, (...args: unknown[]) => void>)[logLevel];
          if (logFn) {
            logFn.call(this.log, logMessage);
          } else {
            this.log.warn(`[Worker][unknown level: ${logLevel}] ${logMessage}`);
          }
          break;
        }
        case 'destroyed':
          this.log.info('Render Worker destroyed');
          break;
        case 'imageSourceLoaded':
          this.log.info(`Worker imageSource loaded: id=${data.id}, ${data.width}x${data.height}`);
          break;
        case 'imageSourceError':
          this.log.warn(`Worker imageSource error: id=${data.id}, ${data.message}`);
          break;
      }
    };

    this._renderWorker.onerror = (event: ErrorEvent) => {
      this.log.error(`Render Worker crashed: ${event.message}`);
      this._handleWorkerCrash();
    };

    this._renderWorker.onmessageerror = () => {
      this.log.error('Render Worker message error');
    };

    // Also create a fallback 2d context for non-Worker operations (e.g., node-level canvas)
    // Note: in Worker mode, rendering is done in the Worker, main thread canvas has no context
    this.ctx = null;
  }

  /**
   * Initialize standard main thread 2d rendering mode.
   */
  private _initMainThreadMode(options: { alpha: boolean; willReadFrequently?: boolean }) {
    this.ctx = this._canvas!.getContext('2d', { alpha: options.alpha, willReadFrequently: options.willReadFrequently });
    if (!this.ctx) {
      throw new RtcError({
        code: ErrorCode.VIDEO_MANAGER_ERROR,
        extraCode: VideoContextErrorCode.NOTSUPPORTED,
        message: '2d context not supported'
      });
    }
    this._contextLostHandler = () => {
      this.log.error('2d context lost, pausing rendering');
    };
    this._contextRestoredHandler = () => {
      this.log.warn('2d context restored, attempting recovery');
      // Try to re-acquire the 2d context
      const newCtx = this._canvas?.getContext('2d', { alpha: this.hasAlpha, willReadFrequently: false });
      if (newCtx) {
        this.ctx = newCtx;
        this.log.info('2d context successfully recovered');
      } else {
        this.log.error('2d context recovery failed, destroying');
        this.destroy(new RtcError({
          code: ErrorCode.VIDEO_MANAGER_ERROR,
          extraCode: VideoContextErrorCode.CONTEXT_LOST,
          message: '2d context lost and recovery failed'
        }));
      }
    };
    this._canvas!.addEventListener('contextlost', this._contextLostHandler);
    this._canvas!.addEventListener('contextrestored', this._contextRestoredHandler);
  }

  /**
   * Set a callback   * Set a callback to be invoked when a Worker stream error occurs.
   * Used by VideoDestinationNode to handle per-source degradation.
   */
  setStreamErrorCallback(callback: ((id: string, message: string) => void) | null) {
    this._streamErrorCallback = callback;
  }

  /**
   * Transfer a MediaStreamTrack's ReadableStream to the Worker for frame extraction.
   * Creates a MediaStreamTrackProcessor, gets its readable stream, and transfers it.
   * @returns true if successfully transferred, false if failed (caller should use main thread extraction)
   */
  addTrackStream(id: string, track: MediaStreamTrack): boolean {
    if (!this._renderWorker || !this._workerReady) {
      this.log.warn(`addTrackStream(${id}): Worker not ready`);
      return false;
    }

    // Don't re-transfer sources that have been degraded to main thread extraction
    if (this._degradedStreams.has(id)) {
      this.log.warn(`addTrackStream(${id}): source was previously degraded, skipping Worker transfer`);
      return false;
    }

    try {
      this.log.debug(`addTrackStream(${id}): creating MediaStreamTrackProcessor, track.id=${track.id}, track.label=${track.label}, track.readyState=${track.readyState}, track.enabled=${track.enabled}, track.muted=${track.muted}`);
      // @ts-ignore - MediaStreamTrackProcessor is not yet in all TS lib definitions
      const processor = new MediaStreamTrackProcessor({ track });
      const readable = processor.readable as ReadableStream<VideoFrame>;

      this._renderWorker.postMessage(
        { type: 'addReadableStream', id, readable },
        [readable]
      );
      this._transferredStreams.add(id);
      // Hold a reference to the processor to prevent GC from stopping the ReadableStream pipeline.
      this._trackProcessors.set(id, processor);
      this.log.info(`addTrackStream(${id}): ReadableStream transferred to Worker`);
      return true;
    } catch (err) {
      this.log.warn(`addTrackStream(${id}) failed: ${(err as Error).message}`);
      return false;
    }
  }

  /**
   * Remove a ReadableStream pipeline from the Worker for a given source.
   */
  removeTrackStream(id: string) {
    if (this._renderWorker && this._workerReady) {
      this._renderWorker.postMessage({ type: 'removeReadableStream', id });
    }
    this._transferredStreams.delete(id);
    this._trackProcessors.delete(id);
    this.log.info(`removeTrackStream(${id}): pipeline removed`);
  }

  /**
   * Replace a source's track stream: remove old pipeline, add new one.
   * @returns true if new stream was successfully transferred
   */
  replaceTrackStream(id: string, newTrack: MediaStreamTrack): boolean {
    this.log.debug(`replaceTrackStream(${id}): removing old stream, then adding new track.id=${newTrack.id}, track.label=${newTrack.label}, track.readyState=${newTrack.readyState}`);
    this.removeTrackStream(id);
    return this.addTrackStream(id, newTrack);
  }

  /**
   * Check if a source has been transferred to Worker for frame extraction.
   */
  isStreamTransferred(id: string): boolean {
    return this._transferredStreams.has(id);
  }

  /**
   * Get the set of source IDs whose streams have been transferred to the Worker.
   */
  get transferredStreams(): ReadonlySet<string> {
    return this._transferredStreams;
  }

  /**
   * Handle Worker crash: completely exit Worker mode, fallback to main thread rendering.
   * Cleans up all Worker resources and notifies downstream consumers to switch output track.
   */
  private _handleWorkerCrash() {
    if (this._workerFailed) return;
    this._workerFailed = true;
    this.log.warn('Worker crashed, completely exiting Worker mode, falling back to main thread rendering');
    this._transferredStreams.clear();
    this._trackProcessors.clear();
    this._cleanupWorkerMode();

    // Rebuild main thread 2d context on a new canvas
    this._canvas = document.createElement('canvas');
    this._canvas.id = `trtc_${this.name}_${VideoContext._ids++}`;
    this._initMainThreadMode({ alpha: this.hasAlpha, willReadFrequently: false });

    // Emit degradation event so downstream consumers (VideoTrackDestination)
    // can get new captureStream track and restart main thread rendering
    this.emit('trackGeneratorDegraded' as any);
  }

  /**
   * Handle TrackGenerator degradation: completely exit Worker mode, fall back to main thread 2d rendering.
   * Called when Worker reports continuous VideoFrame write failures (trackGeneratorFailed event).
   *
   * Degradation flow:
   * 1. Clean up all Worker resources (Worker instance, TrackGenerator, transferred streams)
   * 2. Recreate main thread canvas with 2d context
   * 3. Emit 'trackGeneratorDegraded' event for downstream consumers (VideoTrackDestination)
   *    to switch to captureStream track and restart rendering
   */
  private _handleTrackGeneratorDegradation() {
    if (!this._useTrackGenerator) return;

    this.log.warn('TrackGenerator degradation: completely exiting Worker mode, falling back to main thread 2d rendering');

    // 1. Clean up all Worker resources
    this._transferredStreams.clear();
    this._trackProcessors.clear();
    this._cleanupWorkerMode();

    // 2. Rebuild main thread 2d canvas context on a new canvas
    this._canvas = document.createElement('canvas');
    this._canvas.id = `trtc_${this.name}_${VideoContext._ids++}`;
    this._initMainThreadMode({ alpha: this.hasAlpha, willReadFrequently: false });

    // 3. Emit degradation event for downstream consumers (e.g., VideoTrackDestination)
    //    to get new captureStream track and restart main thread rendering
    this.emit('trackGeneratorDegraded' as any);
  }

  /**
   * Clean up Worker mode resources.
   */
  private _cleanupWorkerMode() {
    // Decrement Blob URL reference count when exiting Worker mode
    if (this._useWorkerRendering) {
      if (Video2dContext._workerBlobUrlRefCount > 0) {
        Video2dContext._workerBlobUrlRefCount--;
      }
      if (Video2dContext._workerBlobUrlRefCount === 0 && Video2dContext._workerBlobUrl !== null) {
        URL.revokeObjectURL(Video2dContext._workerBlobUrl);
        Video2dContext._workerBlobUrl = null;
      }
    }

    // Clean up TrackGenerator resources first
    if (this._generatorTrack) {
      try {
        this._generatorTrack.stop();
      } catch { /* ignore */ }
    }
    this._generatorTrack = null;
    this._useTrackGenerator = false;

    if (this._renderWorker) {
      try {
        this._renderWorker.postMessage({ type: 'destroy' });
      } catch { /* ignore */ }
      this._renderWorker = null;
    }
    this._useWorkerRendering = false;
    this._workerReady = false;
    this._transferredStreams.clear();
    this._trackProcessors.clear();
    this._onWorkerReadyCallback = null;
    if (this._pendingBackgroundImage) {
      this._pendingBackgroundImage.close();
      this._pendingBackgroundImage = null;
    }
    this._pendingImageSourceMessages.clear();
  }

  /**
   * URL for the inline Worker blob, cached for reuse across instances.
   */
  private static _workerBlobUrl: string | null = null;

  /**
   * Reference count for _workerBlobUrl. Tracks how many Video2dContext instances
   * are currently using the Worker Blob URL. When it drops to 0, the Blob URL is revoked.
   */
  private static _workerBlobUrlRefCount = 0;

  /**
   * Create render Worker using externally provided script string.
   * The script is converted to a Blob URL, cached for reuse across instances.
   */
  private _createRenderWorker(workerScript: string): Worker {
    if (!Video2dContext._workerBlobUrl) {
      const blob = new Blob([workerScript], { type: 'application/javascript' });
      Video2dContext._workerBlobUrl = URL.createObjectURL(blob);
    }
    Video2dContext._workerBlobUrlRefCount++;
    return new Worker(Video2dContext._workerBlobUrl);
  }

  /**
   * Register a callback to be invoked when the Worker becomes ready.
   * Used by VideoDestinationNode to trigger pending start() after Worker initialization.
   */
  setOnWorkerReadyCallback(callback: (() => void) | null) {
    this._onWorkerReadyCallback = callback;
  }

  /**
   * Send a message to the render Worker.
   */
  postWorkerMessage(message: any, transfer?: Transferable[]) {
    if (this._renderWorker && this._workerReady) {
      if (transfer) {
        this._renderWorker.postMessage(message, transfer);
      } else {
        this._renderWorker.postMessage(message);
      }
    }
  }

  /**
   * Buffer or send an ImageSource message to the Worker.
   * When Worker is ready, sends immediately via postWorkerMessage.
   * When Worker is not ready, buffers by id so messages are flushed on Worker ready.
   * - addImageSource / updateImageSource: upsert into buffer (later call overwrites earlier)
   * - removeImageSource: delete the buffered entry for that id
   */
  bufferOrPostImageSourceMessage(message: { type: string; id: string; src?: string; animated?: boolean; mimeType?: string }) {
    if (this._renderWorker && this._workerReady) {
      this.postWorkerMessage(message);
    } else {
      if (message.type === 'removeImageSource') {
        this._pendingImageSourceMessages.delete(message.id);
      } else {
        // addImageSource or updateImageSource — upsert by id
        this._pendingImageSourceMessages.set(message.id, message);
      }
    }
  }

  setSize(width: number, height: number) {
    super.setSize(width, height);
    // Notify Worker of size change
    if (this._useWorkerRendering && this._renderWorker) {
      if (this._workerReady) {
        this.postWorkerMessage({ type: 'resize', width, height });
      } else {
        // Buffer size change for after Worker is ready
        this._pendingSize = { width, height };
      }
    }
  }

  /**
   * Set the Worker background color. Buffers if Worker is not yet ready.
   */
  setWorkerBackground(color: string) {
    if (this._workerReady) {
      this.postWorkerMessage({ type: 'setBackground', color });
    } else if (this._useWorkerRendering) {
      // Buffer for after Worker is ready
      this._pendingBackground = color;
      this._pendingBackgroundImage = null;
    }
  }

  /**
   * Set the Worker background image (for CanvasGradient/CanvasPattern support).
   * The ImageBitmap is transferred to the Worker via Transferable.
   * Buffers if Worker is not yet ready.
   */
  setWorkerBackgroundImage(bitmap: ImageBitmap) {
    if (this._workerReady) {
      this.postWorkerMessage({ type: 'setBackgroundImage', bitmap }, [bitmap]);
    } else if (this._useWorkerRendering) {
      // Buffer for after Worker is ready, close old pending bitmap if any
      if (this._pendingBackgroundImage) {
        this._pendingBackgroundImage.close();
      }
      this._pendingBackgroundImage = bitmap;
      this._pendingBackground = null;
    }
  }

  /**
   * Flush any pending state to Worker after it becomes ready.
   */
  private _flushPendingState() {
    if (this._pendingBackgroundImage !== null) {
      this.postWorkerMessage({ type: 'setBackgroundImage', bitmap: this._pendingBackgroundImage }, [this._pendingBackgroundImage]);
      this._pendingBackgroundImage = null;
      this._pendingBackground = null;
    } else if (this._pendingBackground !== null) {
      this.postWorkerMessage({ type: 'setBackground', color: this._pendingBackground });
      this._pendingBackground = null;
    }
    if (this._pendingSize !== null) {
      const { width, height } = this._pendingSize;
      super.setSize(width, height);
      this.postWorkerMessage({ type: 'resize', width, height });
      this._pendingSize = null;
    }
    // Flush buffered ImageSource messages
    if (this._pendingImageSourceMessages.size > 0) {
      for (const msg of this._pendingImageSourceMessages.values()) {
        this.postWorkerMessage(msg);
      }
      this._pendingImageSourceMessages.clear();
    }
  }

  @ChangeState('created', FSM.INIT, { ignoreError: true, sync: true })
  destroy(error?: RtcError) {
    let reason = '';
    if (error) {
      reason = error.message;
      this.error = error;
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_VIDEO_MANAGER.VIDEO_CONTEXT_2D_RESULT, error });
    }
    this.disconnect();
    this.log.info(`video context destroy ${reason ? `: ${reason}` : ''}`);

    this._cleanupWorkerMode();

    // Remove context lost/restored listeners
    if (this._canvas && this._contextLostHandler) {
      this._canvas.removeEventListener('contextlost', this._contextLostHandler);
    }
    if (this._canvas && this._contextRestoredHandler) {
      this._canvas.removeEventListener('contextrestored', this._contextRestoredHandler);
    }
    this._contextLostHandler = null;
    this._contextRestoredHandler = null;

    delete this.ctx;
    if (this._canvas) {
      this._canvas.remove();
      this._canvas.width = 0;
      this._canvas.height = 0;
      delete this._canvas;
    }
    this.removeAllListeners();
    kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_VIDEO_MANAGER.VIDEO_CONTEXT_2D_RESULT });
  }
}
